import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import {
    selectAutomaticTranslationPreference,
    selectPreferredLanguage
} from 'tradera-state/language/selectors';
import { useTranslator } from 'tradera-lang/translate';
import TranslateIcon from '@tradera/blueprint/theme/icons2/translate.svg?jsx';
import qs from 'qs';
import styles from './translation-strategy.module.scss';

const PlainTranslationStrategy = ({
    children,
    discoverTranslationResult,
    language,
    handleTranslationStrategyClick
}) => {
    const { t } = useTranslator();
    const {
        isInternationalSearchLightEnabled,
        query,
        translatedQuery,
        providerName: providerNameFromResult
    } = discoverTranslationResult;
    const automaticTranslationPreference = useSelector(
        selectAutomaticTranslationPreference
    );
    const [providerName, setProviderName] = useState(providerNameFromResult);

    useEffect(() => {
        if (!providerName) {
            setProviderName(providerNameFromResult);
        }
    }, [providerName, providerNameFromResult, setProviderName]);

    const search = qs.parse(location.search);
    const overrideAutomaticTranslationPreference =
        search.overrideAutomaticTranslationPreference === 'true';

    let current;
    if (automaticTranslationPreference) {
        if (!overrideAutomaticTranslationPreference) {
            current = {
                currentCase: 'useTranslatedQuery',
                nextQuery: search.originalQuery || translatedQuery
            };
        } else {
            current = {
                currentCase: 'useOriginalQuery',
                nextQuery: search.originalQuery
            };
        }
    } else {
        if (!overrideAutomaticTranslationPreference) {
            current = {
                currentCase: 'useOriginalQuery',
                nextQuery: translatedQuery
            };
        } else {
            current = {
                currentCase: 'useTranslatedQuery',
                nextQuery: search.originalQuery
            };
        }
    }
    if (!isInternationalSearchLightEnabled || query === current.nextQuery) {
        current = {
            currentCase: 'disabled'
        };
    }

    const { currentCase, nextQuery } = current;

    const isDisabled = currentCase === 'disabled';
    useEffect(() => {
        if (!isDisabled) {
            GtmService.trackAction('Search', 'Search Light Load', query);
        }
    }, [isDisabled, query]);

    const onClick = () => {
        GtmService.trackAction(
            'Search',
            currentCase === 'useTranslatedQuery'
                ? 'Search Light Reset Click'
                : 'Search Light Click',
            language.languageCodeIso2
        );
        handleTranslationStrategyClick(
            nextQuery,
            query,
            !overrideAutomaticTranslationPreference
        );
    };

    if (currentCase === 'useOriginalQuery')
        return (
            <div className="w-100 d-flex align-items-center justify-content-center justify-content-lg-start flex-column flex-md-row my-2">
                {children}

                <span className="size-paris ml-md-2">
                    <TranslateIcon className="icon mr-1" />
                    <button className="btn btn-link p-0" onClick={onClick}>
                        {t('discover_searchLight_buttonText')}
                    </button>
                </span>
            </div>
        );
    if (currentCase === 'useTranslatedQuery')
        return (
            <div className="d-flex flex-column align-items-center align-items-lg-start w-100 my-2">
                <div className="w-100 d-flex align-items-center justify-content-center justify-content-lg-start flex-column flex-md-row my-1">
                    {children}
                    {providerName === 'Google Translate' && (
                        <span className="ml-1 d-flex align-items-baseline">
                            ({t('discover_searchLight_translated_by')}
                            <img
                                src="/static/images/icons/google-greyscale-short.svg"
                                className={styles.translationStrategyGoogleLogo}
                            />
                            &nbsp;)
                        </span>
                    )}
                </div>
                <div className="size-paris">
                    {t('discover_searchLight_from', {
                        originalQuery: nextQuery
                    })}
                    <TranslateIcon className="icon mx-1" />
                    <button className="btn btn-link p-0" onClick={onClick}>
                        {t('discover_searchLight_resetButtonText')}
                    </button>
                </div>
            </div>
        );
    return (
        <div className="w-100 d-flex align-items-center justify-content-center justify-content-lg-start flex-column flex-md-row my-2">
            {children}
        </div>
    );
};

export const TranslationStrategy = connect(state => ({
    discoverTranslationResult: state.discover.discoverTranslationResult,
    language: selectPreferredLanguage(state)
}))(PlainTranslationStrategy);
